/**
 * @file useMetricColorScale.ts
 * @description React hook and utilities for creating and managing metric color scales.
 */

import { useMemo } from 'react';
import * as d3 from 'd3';
import { useDataStore, useVizStore } from '@/store';
import type { MetricColorScheme } from '@/types';
import type { DistrictDict } from '@/types';

/**
 * D3 sequential color schemes mapped by name.
 */
const SEQUENTIAL_SCHEMES: Record<MetricColorScheme, readonly (readonly string[])[]> = {
  Blues: d3.schemeBlues,
  Greens: d3.schemeGreens,
  Oranges: d3.schemeOranges,
  Purples: d3.schemePurples,
  Reds: d3.schemeReds,
  Greys: d3.schemeGreys,
};

/**
 * D3 sequential interpolator functions mapped by color scheme name.
 * Used for continuous gradient scales.
 */
const SEQUENTIAL_INTERPOLATORS: Record<MetricColorScheme, (t: number) => string> = {
  Blues: d3.interpolateBlues,
  Greens: d3.interpolateGreens,
  Oranges: d3.interpolateOranges,
  Purples: d3.interpolatePurples,
  Reds: d3.interpolateReds,
  Greys: d3.interpolateGreys,
};

/**
 * Minimum number of bins allowed for metric color schemes.
 */
export const MIN_METRIC_BINS = 3;

/**
 * Maximum number of bins allowed for metric color schemes.
 */
export const MAX_METRIC_BINS = 9;

/**
 * Gets color array for a metric color scheme with the specified number of bins.
 *
 * Attempts to get the exact bin count from the scheme, falling back to the closest
 * available bin count if the exact count is not available.
 *
 * @param scheme - The color scheme name (e.g., 'Blues', 'Greys')
 * @param requestedBins - The desired number of bins
 * @returns Array of color strings, or null if no valid colors found
 */
function getMetricSchemeColors(
  scheme: MetricColorScheme,
  requestedBins: number,
): readonly string[] | null {
  const palette = SEQUENTIAL_SCHEMES[scheme] ?? d3.schemeGreys;
  const bins = Math.max(
    MIN_METRIC_BINS,
    Math.min(MAX_METRIC_BINS, requestedBins || MIN_METRIC_BINS),
  );

  const direct = palette[bins];
  if (direct && direct.length) return direct;

  for (let i = palette.length - 1; i >= 0; i--) {
    const candidate = palette[i];
    if (candidate && candidate.length) {
      return candidate;
    }
  }

  const fallback = d3.schemeGreys[bins];
  return fallback && fallback.length ? fallback : null;
}

/**
 * Extracts all valid metric values from a district dictionary.
 *
 * Filters out undefined, null, and NaN values, returning only valid numbers.
 *
 * @param districts - Dictionary of districts with metric data
 * @returns Array of numeric metric values
 */
function extractMetricValues(districts: DistrictDict): number[] {
  return Object.values(districts)
    .map((d) => d.metricValue)
    .filter((v): v is number => typeof v === 'number' && !isNaN(v));
}

/**
 * Data structure containing a metric color scale and related information.
 */
export interface MetricScaleData {
  /** D3 quantize or sequential scale for mapping metric values to colors */
  scale: d3.ScaleQuantize<string> | d3.ScaleSequential<string, never> | null;
  /** Array of color strings used in the scale (null for gradient mode) */
  colors: readonly string[] | null;
  /** Minimum metric value in the domain */
  min: number | null;
  /** Maximum metric value in the domain */
  max: number | null;
  /** Array of bin threshold values (only included if requested and useBins is true) */
  thresholds: number[] | null;
  /** Whether this scale uses bins (true) or gradient (false) */
  useBins: boolean;
}

/**
 * Creates a D3 color scale for metric values, either quantized (bins) or sequential (gradient).
 *
 * When useBins is true:
 * 1. Gets the appropriate color scheme colors
 * 2. Creates a quantize scale that divides the domain into equal-width bins
 * 3. Optionally calculates bin thresholds for legend display
 *
 * When useBins is false:
 * 1. Gets the appropriate color scheme interpolator
 * 2. Creates a sequential scale that maps values to a continuous gradient
 *
 * @param metricValues - Array of numeric metric values
 * @param colorScheme - The color scheme to use (e.g., 'Blues', 'Greys')
 * @param binNum - Number of bins to create (only used when useBins is true)
 * @param useBins - Whether to use discrete bins (true) or continuous gradient (false)
 * @param includeThresholds - Whether to calculate bin threshold values (default: false, only used when useBins is true)
 * @returns MetricScaleData object with scale, colors, domain, and optional thresholds
 */
function createMetricColorScale(
  metricValues: number[],
  colorScheme: MetricColorScheme,
  binNum: number,
  useBins: boolean,
  includeThresholds = false,
): MetricScaleData {
  if (metricValues.length === 0) {
    return { scale: null, colors: null, min: null, max: null, thresholds: null, useBins };
  }

  const [min, max] = d3.extent(metricValues) as [number, number];

  if (useBins) {
    // Bins mode: use quantize scale
    const colors = getMetricSchemeColors(colorScheme, binNum);
    if (!colors) {
      return { scale: null, colors: null, min: null, max: null, thresholds: null, useBins };
    }

    const scale = d3
      .scaleQuantize<string>()
      .domain([min, max])
      .range([...colors]);

    let thresholds: number[] | null = null;
    if (includeThresholds) {
      const numBins = colors.length;
      const binWidth = (max - min) / numBins;
      thresholds = [];
      for (let i = 0; i <= numBins; i++) {
        thresholds.push(min + i * binWidth);
      }
    }

    return { scale, colors, min, max, thresholds, useBins };
  } else {
    // Gradient mode: use sequential scale
    const interpolator = SEQUENTIAL_INTERPOLATORS[colorScheme] ?? d3.interpolateGreys;
    const scale = d3.scaleSequential<string, never>().domain([min, max]).interpolator(interpolator);

    return { scale, colors: null, min, max, thresholds: null, useBins };
  }
}

/**
 * Hook that creates a metric color scale based on current store state.
 *
 * This hook:
 * - Extracts metric values from the districts in the data store
 * - Gets color scheme, bin number, and useBins setting from the visualization store
 * - Creates either a quantize (bins) or sequential (gradient) color scale
 * - Only performs calculations when the scale is active (e.g., when metric mode is enabled)
 *
 * @param active - Whether the metric color scale should be active
 * @param includeThresholds - Whether to calculate bin threshold values for legends (default: false, only used when useBins is true)
 * @returns MetricScaleData object containing the scale, colors, domain, and optional thresholds
 *
 * @example
 * ```tsx
 * // For map rendering (no thresholds needed)
 * const polyScale = useMetricColorScale(polyColor === 'metric');
 *
 * // For legend display (thresholds needed when useBins is true)
 * const legendData = useMetricColorScale(showMetricLegend, true);
 * ```
 */
export function useMetricColorScale(active: boolean, includeThresholds = false): MetricScaleData {
  const districts = useDataStore((s) => s.districts);
  const metricColorScheme = useVizStore((s) => s.metricColorScheme);
  const binNum = useVizStore((s) => s.binNum);
  const useBins = useVizStore((s) => s.useBins);

  return useMemo(() => {
    if (!active) {
      return {
        scale: null,
        colors: null,
        min: null,
        max: null,
        thresholds: null,
        useBins: useBins ?? true,
      };
    }

    const metricValues = extractMetricValues(districts);
    return createMetricColorScale(
      metricValues,
      metricColorScheme ?? 'Greys',
      binNum ?? MIN_METRIC_BINS,
      useBins ?? true,
      includeThresholds,
    );
  }, [districts, active, metricColorScheme, binNum, useBins, includeThresholds]);
}
